import { useEffect, useState } from "react";
import { Loader2, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Panel } from "@/components/crm/Badges";

const ConfirmDialog = ({ step, count, onCancel, onNext }) => {
  const [word, setWord] = useState("");
  useEffect(() => setWord(""), [step]);
  return (
    <Dialog open={step > 0} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent data-testid="test-cleanup-confirm-dialog">
        <DialogHeader>
          <DialogTitle>{step === 1 ? `¿Eliminar ${count} leads de prueba?` : "Confirmación final"}</DialogTitle>
          <DialogDescription>
            {step === 1 ? "Se eliminarán del CRM junto con sus conversaciones, notas, tareas, citas e historial. En la plataforma de mensajería solo se elimina el contacto si su nombre sigue siendo de prueba."
              : "Esta acción no se puede deshacer. Escribe ELIMINAR para continuar."}
          </DialogDescription>
        </DialogHeader>
        {step === 2 && <Input value={word} onChange={(e) => setWord(e.target.value)} placeholder="ELIMINAR" data-testid="test-cleanup-confirm-input" />}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="test-cleanup-cancel-button">Cancelar</Button>
          <Button className="bg-rose-600 hover:bg-rose-700" disabled={step === 2 && word !== "ELIMINAR"} onClick={() => onNext(word)}
                  data-testid={step === 1 ? "test-cleanup-continue-button" : "test-cleanup-final-button"}>
            {step === 1 ? "Continuar" : "Eliminar definitivamente"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ l, checked, onToggle }) => (
  <tr className="border-t border-slate-100" data-testid={`test-lead-row-${l.id}`}>
    <td className="py-2 pr-2"><Checkbox checked={checked} onCheckedChange={onToggle} data-testid={`test-lead-check-${l.id}`} /></td>
    <td className="py-2 pr-3 font-medium">{l.name}</td>
    <td className="py-2 pr-3">{l.phone || "—"}</td>
    <td className="py-2 pr-3">{l.channel}</td>
    <td className="py-2 pr-3">{l.seller_name || "Sin asignar"}</td>
    <td className="py-2 pr-3 text-slate-500">{fmtDateTime(l.created_at)}</td>
  </tr>
);

export const TestLeadsCleanupPanel = () => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [step, setStep] = useState(0);
  const [job, setJob] = useState(null);

  const load = () => api.get("/admin/test-leads").then((r) => { setData(r.data); setSel(new Set(r.data.items.map((i) => i.id))); })
    .catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!job || job.status === "done") return undefined;
    const t = setInterval(() => api.get(`/admin/test-leads/jobs/${job.id}`).then((r) => {
      setJob(r.data);
      if (r.data.status === "done") { toast.success(`${r.data.deleted} leads de prueba eliminados`); load(); }
    }).catch(() => {}), 1500);
    return () => clearInterval(t);
  }, [job]);

  const run = async (confirm) => {
    setStep(0);
    try {
      const { data: r } = await api.post("/admin/test-leads/delete", { lead_ids: [...sel], confirm });
      setJob({ id: r.job_id, status: "running", processed: 0, total: sel.size });
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const running = job && job.status !== "done";

  return (
    <Panel title="Limpiar leads de prueba" icon={Trash2} testid="test-cleanup-panel">
      <p className="text-sm text-slate-600">Busca leads creados por pruebas antiguas. Solo coinciden nombres exactos de prueba:</p>
      <ul className="mt-2 flex flex-wrap gap-2" data-testid="test-cleanup-patterns">
        {(data?.patterns || []).map((p) => <li key={p} className="rounded-full bg-[#F5F7FA] px-3 py-1 text-xs text-slate-600">{p}</li>)}
      </ul>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={load} className="rounded-xl" data-testid="test-cleanup-search-button"><Search className="mr-1.5 h-4 w-4" /> Buscar de nuevo</Button>
        <Button onClick={() => setStep(1)} disabled={!sel.size || running} className="rounded-xl bg-rose-600 hover:bg-rose-700" data-testid="test-cleanup-delete-button">
          {running ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />} Eliminar seleccionados ({sel.size})
        </Button>
        {job && <span className="text-sm text-slate-600" data-testid="test-cleanup-progress">
          {running ? `Eliminando… ${job.processed || 0} / ${job.total}` : `Listo: ${job.deleted} eliminados · ${job.platform_deleted} contactos eliminados en la plataforma · ${job.platform_kept} conservados`}
        </span>}
      </div>
      {data && (data.items.length === 0 ? <p className="mt-4 rounded-xl bg-[#F5F7FA] p-4 text-sm text-slate-500" data-testid="test-cleanup-empty">No hay leads de prueba.</p> : (
        <div className="mt-4 max-h-[420px] overflow-auto">
          <table className="w-full text-left text-sm" data-testid="test-cleanup-table">
            <thead className="text-xs uppercase text-slate-400"><tr><th /><th>Nombre</th><th>Teléfono</th><th>Canal</th><th>Vendedor</th><th>Creado</th></tr></thead>
            <tbody>{data.items.map((l) => <Row key={l.id} l={l} checked={sel.has(l.id)} onToggle={() => toggle(l.id)} />)}</tbody>
          </table>
        </div>
      ))}
      {data?.last_run && <p className="mt-3 text-xs text-slate-400" data-testid="test-cleanup-last-run">Última limpieza: {data.last_run.user_name} · {fmtDateTime(data.last_run.created_at)} · {data.last_run.deleted} eliminados</p>}
      <ConfirmDialog step={step} count={sel.size} onCancel={() => setStep(0)} onNext={(w) => (step === 1 ? setStep(2) : run(w))} />
    </Panel>
  );
};
